/**
 * /account の「ログイン方法」の欄（2026-09-27 creator/dev-oauth-ui）。通知メール「ログイン方法が追加 / 削除されました」のボタンはこの画面に向く。
 *
 * API の正: apps/server/src/modules/platform/oauth/identities.ts・flow.ts
 *   GET    /v1/developers/me/identities           → { hasPassword, identities: [{ provider, email, emailVerified, createdAt, lastUsedAt }] }
 *   DELETE /v1/developers/me/identities/:provider  { password } | { reauthToken } → { hasPassword, identities }（最後の手段は 409 last_login_method）
 *   POST   /v1/developers/me/password             { newPassword, currentPassword } → { ok, hasPassword: true }
 *          （2026-09-28 Core (B)・SEC-DEVLOGIN-04: 変更は今のパスワードが必須・kdra_ は受け付けない。パスワードの無い人は 403 password_reset_required）
 *   POST   /v1/developers/password/forgot         { email } → パスワードの無い人の設定は「登録メールに設定用のリンクを送る」だけ
 *   追加 = POST /v1/developers/oauth/:provider/start { intent: 'link', password | reauthToken } → プロバイダ → /oauth/callback → ここへ戻る
 *
 * 再認証は共通の部品（components/Reauth.tsx）。「Google で確認」から戻ったら、操作の名前（action）で続きを実行する:
 *   'unlink:<p>' → 自動で外す（外す確認は出る前に済んでいる）/ 'link:<p>' → 自動で追加の往復を始める
 * パスワードは再認証の部品を使わない（2026-09-28 (B)）: 持っている人の変更 = ダイアログの今のパスワードの欄だけ。
 *   持っていない人（Google などだけ）= 「リンクをメールで送る」（../emailVerify.ts passwordAction の WHY: kdra_ は XSS で取れる・受信トレイは越えられない）。
 *   以前の 'password' の往復（Google で確かめてから kdra_ で設定）は、サーバーが受け付けなくなったので消した
 *
 * 外す・パスワードを設定 / 変更すると、サーバーは今のセッション以外の全セッションと MCP の接続（kdt_）を失効する → 確認の文に書く。
 * パスワードの設定 / 変更は live の出金の確認を 72 時間止める（パスワード再設定と同じ・乗っ取りの疑いの出来事）→ 同じく書く。
 *
 * 2026-09-28 creator/passkey-login: ログイン用パスキーの欄（./AccountLoginPasskeys.tsx）をこのカードの下に足した。
 *   一覧（GET /v1/developers/me/login-passkeys）はこのカードが読む: 「最後のログイン手段か」（canUnlink にパスキーの数を足す = サーバーの
 *   hasOtherLoginMethod と同じ数え方）と、再認証の方法（loginPasskey: true = ダイアログに「パスキーで確認」）の両方に使うため。
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, type ApiError } from '../api';
import { authFormProps, hiddenUsernameProps, MIN_PASSWORD, passwordFieldProps, passwordLengthOk } from '../authForms';
import { ProviderIcon, useLoginOptions, useOAuthProviders } from '../components/OAuthButtons';
import { useReauth, useReauthFailureText } from '../components/Reauth';
import { ConfirmDialog, ErrorState, Spinner } from '../components/ui';
import { canUnlink, isDevOAuthProvider, isReauthError, PROVIDER_NAMES, type DevOAuthProvider, type ReauthMethods, type ReauthProof } from '../devOAuth';
import { isPasswordResetRequired, passwordAction } from '../emailVerify';
import { startDevOAuth } from '../devOAuthClient';
import { useResource } from '../hooks';
import { errorText, formatDate, useT, type MessageKey } from '../i18n';
import { isMoneyNavHidden } from '../launchHidden';
import { useSession } from '../session';
import type { OAuthAccountNotice } from './OAuthCallback';
import { LoginPasskeysSection, type LoginPasskeysResponse } from './AccountLoginPasskeys';

interface Identity {
  provider: string;
  email: string | null;
  emailVerified: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}
interface IdentitiesResponse {
  hasPassword: boolean;
  identities: Identity[];
}

type Notice = { tone: 'ok' | 'error'; text: string };

export function LoginMethodsCard() {
  const { t } = useT();
  const { developer, updateDeveloper } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const res = useResource<IdentitiesResponse>('/v1/developers/me/identities');
  const configured = useOAuthProviders();
  // ログイン用パスキーの一覧。読めない（古いサーバー = route_missing 等）ときは「0 本」として今までどおり動く（欄は出さない）
  const lpk = useResource<LoginPasskeysResponse>('/v1/developers/me/login-passkeys');
  const loginOptions = useLoginOptions();
  const reauth = useReauth();
  const failureText = useReauthFailureText();
  const [notice, setNotice] = useState<Notice | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmUnlink, setConfirmUnlink] = useState<DevOAuthProvider | null>(null);
  const [passwordDialog, setPasswordDialog] = useState(false);

  // 変更のダイアログが 403 password_reset_required を受けた = 画面の hasPassword が古かった。以後はリンクを送る形にする
  const [noPasswordOverride, setNoPasswordOverride] = useState(false);

  const data = res.data;
  const hasPassword = !noPasswordOverride && (data?.hasPassword ?? developer?.hasPassword !== false);
  const linked = (data?.identities ?? []).filter((i) => isDevOAuthProvider(i.provider));
  const loginPasskeyCount = lpk.data?.passkeys.length ?? 0;
  const methods: ReauthMethods = {
    hasPassword,
    providers: linked.map((i) => i.provider as DevOAuthProvider),
    ...(loginPasskeyCount > 0 ? { loginPasskey: true } : {})
  };
  // 並び: サーバーで使えるもの（google / apple / github の順）+ 付いているのにサーバーの設定から外れたもの（外せるように出す）
  const rows = [...configured, ...methods.providers.filter((p) => !configured.includes(p))];

  // /oauth/callback からの案内（追加できた / できなかった）。読んだら履歴の state から消す（再読み込みで同じ案内を出さない）
  useEffect(() => {
    const n = (location.state as { oauthNotice?: OAuthAccountNotice } | null)?.oauthNotice;
    if (!n) return;
    const name = PROVIDER_NAMES[n.provider];
    setNotice(n.kind === 'linked' ? { tone: 'ok', text: t('login.linked', { provider: name }) } : { tone: 'error', text: t(`oauth.error.${n.code}` as MessageKey, { provider: name }) });
    navigate(location.pathname, { replace: true, state: null });
  }, []);

  const applyIdentities = (r: IdentitiesResponse) => {
    res.reload();
    updateDeveloper({ hasPassword: r.hasPassword });
  };

  /** 外す（確認のダイアログの後）。戻ってきた証明があれば run が使う = 自動で続く */
  const unlink = async (provider: DevOAuthProvider) => {
    setBusy(`unlink:${provider}`);
    setNotice(null);
    try {
      const r = await reauth.run(
        (proof) => api<IdentitiesResponse>(`/v1/developers/me/identities/${provider}`, { method: 'DELETE', body: proof }),
        { action: `unlink:${provider}`, returnTo: '/account', ask: methods }
      );
      if (r) {
        applyIdentities(r);
        setNotice({ tone: 'ok', text: t('login.unlinked', { provider: PROVIDER_NAMES[provider] }) });
      }
    } catch (err) {
      setNotice({ tone: 'error', text: errorText(err as ApiError) });
    } finally {
      setBusy(null);
    }
  };

  /** 追加（再認証 → 追加の往復を始める = プロバイダの画面へ移る） */
  const link = async (provider: DevOAuthProvider) => {
    setBusy(`link:${provider}`);
    setNotice(null);
    try {
      const r = await reauth.run((proof) => startDevOAuth({ provider, intent: 'link', returnTo: '/account', proof: 'password' in proof || 'reauthToken' in proof ? (proof as ReauthProof) : undefined }).then(() => true), {
        action: `link:${provider}`,
        returnTo: '/account',
        ask: methods
      });
      // 成功 = ページが移る。取り消した（null）ときだけ押せる状態に戻す
      if (!r) setBusy(null);
    } catch (err) {
      setNotice({ tone: 'error', text: errorText(err as ApiError) });
      setBusy(null);
    }
  };

  // 「Google で確認」から戻った: 操作の名前で続ける（1 回だけ）
  const resumedOnce = useRef(false);
  useEffect(() => {
    const p = reauth.pending;
    if (!p || resumedOnce.current || !res.data) return;
    const [kind, prov] = p.action.split(':');
    if (kind === 'unlink' && isDevOAuthProvider(prov)) {
      resumedOnce.current = true;
      void unlink(prov);
    } else if (kind === 'link' && isDevOAuthProvider(prov)) {
      resumedOnce.current = true;
      void link(prov);
    }
  }, [reauth.pending, res.data]);

  // 戻りで失敗した（別のアカウントを選んだ・キャンセル）
  useEffect(() => {
    const f = reauth.failure;
    if (!f || !/^(unlink:|link:)/.test(f.action)) return;
    setNotice({ tone: 'error', text: failureText(f) });
    reauth.consume();
  }, [reauth.failure]);

  /**
   * パスワードの行のボタン（../emailVerify.ts passwordAction）。持っている人 = 変更のダイアログ（今のパスワード）。
   * 持っていない人 = 登録メールに設定用のリンクを送る（POST /v1/developers/password/forgot。常に 200 = 送れたかは受信トレイで分かる）
   */
  const openPassword = async () => {
    setNotice(null);
    if (passwordAction(hasPassword) === 'change') {
      setPasswordDialog(true);
      return;
    }
    if (!developer) return;
    setBusy('password');
    try {
      // ログイン不要の口（auth: false = 401 でログアウトさせる扱いの外）。宛先は自分の登録メールだけ（入力させない）
      await api('/v1/developers/password/forgot', { method: 'POST', body: { email: developer.email }, auth: false });
      setNotice({ tone: 'ok', text: t('login.setLinkSent', { email: developer.email }) });
    } catch (err) {
      setNotice({ tone: 'error', text: errorText(err as ApiError) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="card" id="login-methods">
      <h2 className="card-title">{t('login.title')}</h2>
      <p className="muted small">{t('login.lead')}</p>
      {reauth.dialog}
      {notice && (
        <div className={`alert ${notice.tone === 'ok' ? 'alert-ok' : 'alert-error'}`} role={notice.tone === 'ok' ? 'status' : 'alert'}>
          {notice.text}
        </div>
      )}
      {res.loading && !data && <Spinner />}
      {res.error && <ErrorState error={res.error} onRetry={res.reload} />}
      {data && (
        <ul className="login-methods">
          <li className="login-method">
            <span className="login-method-name">
              <span className="login-method-icon" aria-hidden="true">
                <KeyIcon />
              </span>
              <strong>{t('login.password')}</strong>
              <span className="muted small">{hasPassword ? t('login.passwordSet') : t('login.passwordNotSet')}</span>
              {!hasPassword && developer && <span className="muted small">{t('login.setViaEmail', { email: developer.email })}</span>}
            </span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => void openPassword()} disabled={!!busy}>
              {busy === 'password' ? t('common.working') : hasPassword ? t('login.changePassword') : t('login.sendSetLink')}
            </button>
          </li>
          {rows.map((p) => {
            const idn = linked.find((i) => i.provider === p);
            const name = PROVIDER_NAMES[p];
            const removable = canUnlink({ hasPassword, linkedCount: linked.length, loginPasskeyCount });
            return (
              <li key={p} className="login-method">
                <span className="login-method-name">
                  <span className="login-method-icon">
                    <ProviderIcon provider={p} />
                  </span>
                  <strong>{name}</strong>
                  {idn ? (
                    <span className="muted small">
                      {idn.email ?? '—'} · {t('login.addedOn', { date: formatDate(idn.createdAt) })}
                      {idn.lastUsedAt ? ` · ${t('login.lastUsed', { date: formatDate(idn.lastUsedAt) })}` : ''}
                    </span>
                  ) : (
                    <span className="muted small">{t('login.notLinked')}</span>
                  )}
                </span>
                {idn ? (
                  <span className="login-method-actions">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmUnlink(p)} disabled={!removable || !!busy} aria-describedby={removable ? undefined : 'login-last-note'}>
                      {busy === `unlink:${p}` ? t('common.working') : t('login.remove')}
                    </button>
                  </span>
                ) : configured.includes(p) ? (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => void link(p)} disabled={!!busy}>
                    {busy === `link:${p}` ? t('common.working') : t('login.add', { provider: name })}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {data && !canUnlink({ hasPassword, linkedCount: linked.length, loginPasskeyCount }) && linked.length > 0 && (
        <p id="login-last-note" className="small muted">
          {t('login.lastMethod')}
        </p>
      )}
      <p className="small muted">{t('login.noAutoLink')}</p>
      {data && (
        <LoginPasskeysSection
          passkeys={lpk.data?.passkeys ?? null}
          methods={methods}
          linkedCount={linked.length}
          available={loginOptions.passkeyLogin}
          onChanged={lpk.reload}
        />
      )}

      <ConfirmDialog
        open={!!confirmUnlink}
        title={confirmUnlink ? t('login.removeTitle', { provider: PROVIDER_NAMES[confirmUnlink] }) : ''}
        confirmLabel={t('login.remove')}
        tone="danger"
        onClose={() => setConfirmUnlink(null)}
        onConfirm={() => {
          const p = confirmUnlink;
          setConfirmUnlink(null);
          if (p) void unlink(p);
        }}
      >
        <p>{confirmUnlink ? t('login.removeBody', { provider: PROVIDER_NAMES[confirmUnlink] }) : null}</p>
        <p className="small">{t('login.signOutOthers')}</p>
      </ConfirmDialog>

      {passwordDialog && developer && (
        <SetPasswordDialog
          email={developer.email}
          onClose={() => setPasswordDialog(false)}
          onResetRequired={() => {
            // サーバーの判断ではパスワードが無い（別の端末で外した・画面が古い）= メールのリンクの道へ切り替える
            setPasswordDialog(false);
            setNoPasswordOverride(true);
            updateDeveloper({ hasPassword: false });
            setNotice({ tone: 'error', text: t('login.setViaEmail', { email: developer.email }) });
          }}
          onDone={() => {
            setPasswordDialog(false);
            updateDeveloper({ hasPassword: true });
            res.reload();
            setNotice({ tone: 'ok', text: t('login.passwordSaved') });
            // 成功したら History API の遷移を 1 回（パスワード管理は「送信 → 遷移」で保存を出す・authForms.ts の冒頭）
            navigate('/account', { replace: true });
          }}
        />
      )}
    </section>
  );
}

/**
 * パスワードの変更のダイアログ（持っている人だけ・2026-09-28 (B) で設定は外した）。パスワード管理が扱える形（post のフォーム・見えないアカウント名・
 * 今のパスワード = current-password・新しいパスワード = new-password と確認の欄）。属性は authForms.ts。
 * 「ほかの端末がログアウトされる」「72 時間」の文は、このダイアログに残す（管理の指示）
 */
function SetPasswordDialog({
  email,
  onClose,
  onDone,
  onResetRequired
}: {
  email: string;
  onClose: () => void;
  onDone: () => void;
  /** 403 password_reset_required（サーバーの判断ではパスワードが無い）→ メールのリンクの道へ */
  onResetRequired: () => void;
}) {
  const { t } = useT();
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!passwordLengthOk(next)) return setError(t('auth.passwordTooShort', { min: MIN_PASSWORD }));
    if (next !== confirm) return setError(t('recover.mismatch'));
    if (!current) return setError(t('login.currentRequired'));
    setBusy(true);
    try {
      await api('/v1/developers/me/password', { method: 'POST', body: { newPassword: next, currentPassword: current } });
      onDone();
    } catch (err) {
      const e2 = err as ApiError;
      if (isPasswordResetRequired(e2)) {
        onResetRequired();
        return;
      }
      if (isReauthError(e2)) {
        // 今のパスワードの誤り（セッションは生きている = api.ts はログアウトさせない）
        setError(t('error.reauthentication_failed'));
        setCurrent('');
      } else {
        setError(errorText(e2));
      }
      setBusy(false);
    }
  };

  return (
    <dialog ref={ref} className="dialog" aria-labelledby={`${id}-t`} onClose={onClose} onCancel={onClose}>
      <form {...authFormProps('setPassword', '/account')} onSubmit={(e) => void submit(e)}>
        <h2 id={`${id}-t`}>{t('login.changePasswordTitle')}</h2>
        <div className="dialog-body">
          <input {...hiddenUsernameProps(email)} />
          <label className="field" htmlFor="sp-current">
            <span>{t('login.currentPassword')}</span>
            <input {...passwordFieldProps('current', { id: 'sp-current' })} value={current} onChange={(e) => setCurrent(e.target.value)} />
          </label>
          <label className="field" htmlFor="sp-new">
            <span>{t('recover.newPassword')}</span>
            <input {...passwordFieldProps('new', { id: 'sp-new' })} value={next} onChange={(e) => setNext(e.target.value)} aria-describedby="sp-hint" />
            <small id="sp-hint" className="muted">
              {t('auth.passwordHint', { min: MIN_PASSWORD })}
            </small>
          </label>
          <label className="field" htmlFor="sp-confirm">
            <span>{t('recover.confirmPassword')}</span>
            <input {...passwordFieldProps('new', { confirm: true, id: 'sp-confirm' })} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </label>
          <p className="small">{t('login.signOutOthers')}</p>
          {/* 出金（payouts）を見せない間は出金の確認の保留の説明も出さない（2026-09-29 creator/launch-money） */}
          {!isMoneyNavHidden('withdraw') && <p className="small">{t('login.withdrawHold')}</p>}
          {error && (
            <p className="alert alert-error" role="alert">
              {error}
            </p>
          )}
        </div>
        <div className="dialog-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} data-cancel>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t('common.working') : t('login.savePassword')}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * パスワードの行のアイコン = 鍵（18px・線）。ほかの行（Google・GitHub のロゴ 18px）と大きさをそろえる（2026-10-18 design/rauno-dashboard2）。
 * 以前は字の黒丸 2 つ（パスワードの伏せ字の形）で、小さく・ロゴと高さが揃わなかった（rauno-audit.md 設定の行）
 */
function KeyIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="15" r="4" />
      <path d="M10.8 12.2L20 3M16 7l3 3M14 9l2 2" />
    </svg>
  );
}
